import type {
  EquipmentInstance,
  EquipmentSlot,
  EquipmentStats,
  EquippedMap,
  ItemDef,
  Player,
  Rarity,
} from './types';
import { getItem } from './items';
import { upgradedStats } from './upgrade';

const RARITY_RANK: Record<Rarity, number> = {
  common: 0, uncommon: 1, rare: 2, epic: 3, legendary: 4, mythic: 5,
};

export function rarityRank(r: Rarity): number {
  return RARITY_RANK[r];
}

/**
 * Stat multiplier per ascension tier.
 *   T0 (not ascended) = 1.0×
 *   T1 = 1.5×
 *   T2 = 2.25× (1.5²)
 *   T3 = 3.375× (1.5³, also flagged Mythic rarity in UI)
 */
const ASCENSION_TIER_MULT: Record<0 | 1 | 2 | 3, number> = {
  0: 1.0, 1: 1.5, 2: 2.25, 3: 3.375,
};

/** Effective ascension tier (0..3), supporting both legacy `ascended:true` and new `ascensionTier`. */
export function getAscensionTier(inst: { ascended?: boolean; ascensionTier?: number }): 0 | 1 | 2 | 3 {
  const tier = inst.ascensionTier ?? (inst.ascended ? 1 : 0);
  return Math.max(0, Math.min(3, tier)) as 0 | 1 | 2 | 3;
}

/** Stat multiplier from ascension tier. */
export function ascensionMult(inst: { ascended?: boolean; ascensionTier?: number }): number {
  return ASCENSION_TIER_MULT[getAscensionTier(inst)];
}

/**
 * Effective rarity: T3 ascended items count as Mythic regardless of base rarity.
 * Used for visual display + power-score weighting + filter UI.
 */
export function effectiveRarity(inst: { ascended?: boolean; ascensionTier?: number }, baseRarity: Rarity): Rarity {
  return getAscensionTier(inst) >= 3 ? 'mythic' : baseRarity;
}

/** Sum of stats across all equipped instances (upgrade-adjusted + rolled + flat bonus stats). */
export function getEquippedStats(
  equipment: EquippedMap,
  itemLookup: (id: string) => ItemDef | undefined = getItem,
): EquipmentStats {
  let attack = 0, defense = 0, health = 0;
  for (const inst of Object.values(equipment)) {
    if (!inst) continue;
    const def = itemLookup(inst.itemId);
    if (!def?.stats) continue;
    const base = inst.rolledStats ?? def.stats;
    const s = upgradedStats(base, inst.upgradeLevel, def.stats);
    const asc = ascensionMult(inst);
    attack += Math.round(s.attack * asc);
    defense += Math.round(s.defense * asc);
    health += Math.round(s.health * asc);
    if (inst.bonusStats) {
      for (const b of inst.bonusStats) {
        if (b.isMultiplier) continue;
        if (b.stat === 'attack') attack += b.value;
        else if (b.stat === 'defense') defense += b.value;
        else if (b.stat === 'health') health += b.value;
      }
    }
  }
  return { attack, defense, health };
}

/** Item power score across all equipped instances (used for leaderboard ranking). */
export function calcEquippedItemPower(equipment: EquippedMap): number {
  let power = 0;
  for (const inst of Object.values(equipment)) {
    if (!inst) continue;
    const def = getItem(inst.itemId);
    if (!def?.stats) continue;
    const base = inst.rolledStats ?? def.stats;
    const s = upgradedStats(base, inst.upgradeLevel, def.stats);
    const asc = ascensionMult(inst);
    let atk = Math.round(s.attack * asc), defStat = Math.round(s.defense * asc), hp = Math.round(s.health * asc);
    if (inst.bonusStats) {
      for (const b of inst.bonusStats) {
        if (b.isMultiplier) continue;
        if (b.stat === 'attack') atk += b.value;
        else if (b.stat === 'defense') defStat += b.value;
        else if (b.stat === 'health') hp += b.value;
      }
    }
    // Ascended items get a rarity-weight bump; T3 = Mythic.
    const effectiveR = rarityRank(effectiveRarity(inst, def.rarity));
    power += atk + defStat + Math.floor(hp / 3) + effectiveR * 4 + inst.upgradeLevel;
  }
  return power;
}

/** Count how many copies of `perk` exist across all equipped Glitch-Perks (additive stacking). */
export function countGlitchPerkStacks(
  equipment: EquippedMap,
  perk: import('./types').GlitchPerk,
): number {
  let count = 0;
  for (const inst of Object.values(equipment)) {
    if (!inst?.glitchPerks) continue;
    for (const p of inst.glitchPerks) if (p === perk) count++;
  }
  return count;
}

/** Total additive multiplier fraction from bonus stat rolls on all equipped items. */
export function getEquippedBonusMult(
  equipment: EquippedMap,
  stat: 'loot_chance' | 'xp_gain',
): number {
  let total = 0;
  for (const inst of Object.values(equipment)) {
    if (!inst?.bonusStats) continue;
    for (const b of inst.bonusStats) {
      if (b.stat === stat && b.isMultiplier) total += b.value;
    }
  }
  return total;
}

/** Equip an instance from the stash into its matching slot. Old instance goes back to stash. */
export function equipInstance(
  player: Player,
  instanceId: string,
  itemLookup: (id: string) => ItemDef | undefined = getItem,
  now: number = Date.now(),
): { player: Player; ok: boolean; reason?: string } {
  const instance = player.equipmentStash.find((i) => i.id === instanceId);
  if (!instance) return { player, ok: false, reason: 'not_in_stash' };

  const def = itemLookup(instance.itemId);
  if (!def?.equipSlot) return { player, ok: false, reason: 'not_equippable' };

  const slot = def.equipSlot;

  // Swap: current equipped → stash, new instance → slot
  const prev = player.equipment[slot];
  const nextStash = player.equipmentStash.filter((i) => i.id !== instanceId);
  if (prev) nextStash.push(prev);

  const nextEquipment: EquippedMap = { ...player.equipment, [slot]: instance };

  return {
    player: { ...player, equipmentStash: nextStash, equipment: nextEquipment, updatedAt: now },
    ok: true,
  };
}

/** Unequip slot → instance goes back to stash. */
export function unequipSlot(
  player: Player,
  slot: EquipmentSlot,
  now: number = Date.now(),
): { player: Player; ok: boolean } {
  const inst = player.equipment[slot];
  if (!inst) return { player, ok: false };

  const nextEquipment: EquippedMap = { ...player.equipment };
  delete nextEquipment[slot];
  const nextStash = [...player.equipmentStash, inst];

  return {
    player: { ...player, equipmentStash: nextStash, equipment: nextEquipment, updatedAt: now },
    ok: true,
  };
}

/** Remove/destroy an instance from stash (e.g., after salvage). */
export function removeFromStash(player: Player, instanceId: string, now: number = Date.now()): Player {
  return {
    ...player,
    equipmentStash: player.equipmentStash.filter((i) => i.id !== instanceId),
    updatedAt: now,
  };
}

/** Replace an instance in the player's equipment or stash (for upgrades). */
export function replaceInstance(
  player: Player,
  newInstance: EquipmentInstance,
  now: number = Date.now(),
): Player {
  // Check equipment slots
  const equipmentEntry = Object.entries(player.equipment).find(
    ([, inst]) => inst?.id === newInstance.id,
  );
  if (equipmentEntry) {
    const [slot] = equipmentEntry;
    const nextEquipment: EquippedMap = { ...player.equipment, [slot]: newInstance };
    return { ...player, equipment: nextEquipment, updatedAt: now };
  }

  // Check stash
  const stashIdx = player.equipmentStash.findIndex((i) => i.id === newInstance.id);
  if (stashIdx >= 0) {
    const nextStash = player.equipmentStash.map((i) => (i.id === newInstance.id ? newInstance : i));
    return { ...player, equipmentStash: nextStash, updatedAt: now };
  }

  return player;
}

export function findInstance(player: Player, instanceId: string): EquipmentInstance | null {
  for (const inst of Object.values(player.equipment)) {
    if (inst?.id === instanceId) return inst;
  }
  return player.equipmentStash.find((i) => i.id === instanceId) ?? null;
}

/** Minimum rarity across all 4 equipped slots (or null if any slot is empty). */
export function minEquippedRarity(
  equipment: EquippedMap,
  itemLookup: (id: string) => ItemDef | undefined = getItem,
): Rarity | null {
  const slots: EquipmentSlot[] = ['weapon', 'helmet', 'armor', 'accessory'];
  let minRank = Infinity;
  let minRarity: Rarity | null = null;
  for (const s of slots) {
    const inst = equipment[s];
    if (!inst) return null; // slot missing
    const def = itemLookup(inst.itemId);
    if (!def) return null;
    const rank = RARITY_RANK[def.rarity];
    if (rank < minRank) {
      minRank = rank;
      minRarity = def.rarity;
    }
  }
  return minRarity;
}
